/**
 * AdminProfilePage - UC-037: Admin Profile View Use
 * Allows admin to view and edit their profile information and notification preferences
 */
import React, { useState, useEffect } from 'react';
import { Button, Input, Card } from '../components/ui';
// React imports only - removing Vue components
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';
import { UserResponse, NotificationPreferencesResponse } from '../types';

interface AdminProfile {
  name: string;
  surname: string;
  email: string;
  role: string;
  lastLogin: string;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  frequency: string;
}

interface ValidationErrors {
  name?: string;
  surname?: string;
  email?: string;
}

export function AdminProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingNotifications, setIsEditingNotifications] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNotificationSuccess, setShowNotificationSuccess] = useState(false);

  const [editForm, setEditForm] = useState<AdminProfile>({
    name: '',
    surname: '',
    email: '',
    role: '',
    lastLogin: ''
  });

  const [notificationForm, setNotificationForm] = useState<NotificationSettings>({
    emailEnabled: false,
    smsEnabled: false,
    frequency: ''
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const frequencyOptions = ['Immediate', 'Daily', 'Weekly', 'Monthly'];

  // Load profile data
  useEffect(() => {
    loadProfile();
    loadNotificationPreferences();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // GET /users/me returns paginated response with user data
      const response = await api.get<{ items?: UserResponse[] }>('/users/me');
      const userData = response.items?.[0];

      if (!userData) {
        throw new Error(t('adminProfile.profileNotFound', 'Profile not found'));
      }

      // Map UserResponse to AdminProfile format
      const profileData: AdminProfile = {
        name: userData.name,
        surname: userData.surname ?? '',
        email: userData.email,
        role: userData.role,
        lastLogin: new Date(userData.registrationDate).toLocaleDateString() // Using registration date as placeholder
      };

      setProfile(profileData);
      // Seed the edit form so entering edit mode pre-fills every field.
      setEditForm(profileData);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError(t('adminProfile.loadError', 'Failed to load profile. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await api.get<{ items?: NotificationPreferencesResponse[] }>('/users/me/notification-preferences');
      const prefs = response.items?.[0];

      if (prefs) {
        setNotifications({
          emailEnabled: prefs.notifyProjectUpdates,
          smsEnabled: prefs.notifyUserRegistrations,
          frequency: 'Immediate' // Default frequency
        });
      }
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      // Set defaults if loading fails
      setNotifications({
        emailEnabled: true,
        smsEnabled: false,
        frequency: 'Immediate'
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!editForm.name.trim()) {
      errors.name = t('adminProfile.nameRequired', 'Name is required');
    }

    if (!editForm.surname.trim()) {
      errors.surname = t('adminProfile.surnameRequired', 'Surname is required');
    }

    if (!editForm.email.trim()) {
      errors.email = t('adminProfile.emailRequired', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      errors.email = t('adminProfile.emailInvalid', 'Please enter a valid email address');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveProfile = async () => {
    if (!validateForm()) return;

    try {
      // Update profile via PUT /users/me. Backend UserUpdate accepts
      // name + surname + email for the self-update flow; role and
      // credit_balance are Optional and intentionally omitted here so
      // an admin can't accidentally change their own role/credits via
      // this screen.
      const updateData = {
        name: editForm.name,
        surname: editForm.surname,
        email: editForm.email,
      };

      await api.put<UserResponse>('/users/me', updateData);

      // Update local state
      if (profile) {
        setProfile({
          ...profile,
          name: editForm.name,
          surname: editForm.surname,
          email: editForm.email,
        });
      }

      setIsEditing(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      toast.success(t('adminProfile.saveSuccess', 'Profile updated successfully'));
    } catch (err) {
      console.error('Failed to save profile:', err);
      toast.error(t('adminProfile.saveError', 'Failed to update profile. Please try again.'));
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setValidationErrors({});
  };

  const saveNotifications = async () => {
    try {
      const updateData = {
        notifyProjectUpdates: notificationForm.emailEnabled,
        notifyUserRegistrations: notificationForm.smsEnabled
      };

      await api.put<NotificationPreferencesResponse>('/users/me/notification-preferences', updateData);

      // Update local state
      if (notifications) {
        setNotifications({
          emailEnabled: notificationForm.emailEnabled,
          smsEnabled: notificationForm.smsEnabled,
          frequency: notificationForm.frequency
        });
      }

      setIsEditingNotifications(false);
      setShowNotificationSuccess(true);
      setTimeout(() => setShowNotificationSuccess(false), 3000);
      toast.success(t('adminProfile.notificationSaveSuccess', 'Notification preferences updated successfully'));
    } catch (err) {
      console.error('Failed to save notifications:', err);
      toast.error(t('adminProfile.notificationSaveError', 'Failed to update notification preferences. Please try again.'));
    }
  };

  const cancelNotificationEdit = () => {
    setIsEditingNotifications(false);
  };

  const startEdit = () => {
    if (profile) {
      setEditForm({ ...profile });
      setIsEditing(true);
    }
  };

  const startNotificationEdit = () => {
    if (notifications) {
      setNotificationForm({ ...notifications });
      setIsEditingNotifications(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadProfile}>
              {t('adminProfile.retry', 'Retry')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!profile || !notifications) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <p className="text-gray-600">{t('adminProfile.noData', 'No profile data available.')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('adminProfile.title', 'Admin Profile')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">{t('adminProfile.subtitle', 'Manage your profile information and notification preferences')}</p>
      </div>

      {/* Profile Information Section */}
      <Card className="mb-5 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('adminProfile.profileInfo', 'Profile Information')}</h2>
          <Button
            onClick={() => isEditing ? cancelEdit() : startEdit()}
            variant="primary"
          >
            {isEditing ? t('adminProfile.cancel', 'Cancel') : t('adminProfile.editProfile', 'Edit Profile')}
          </Button>
        </div>

        {!isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.name', 'Name')}</label>
                <p className="text-base sm:text-sm text-gray-900">{profile.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.surname', 'Surname')}</label>
                <p className="text-base sm:text-sm text-gray-900">{profile.surname}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.email', 'Email')}</label>
                <p className="text-base sm:text-sm text-gray-900">{profile.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.role', 'Role')}</label>
                <p className="text-base sm:text-sm text-gray-900">{profile.role}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.lastLogin', 'Last Login')}</label>
              <p className="text-base sm:text-sm text-gray-900">{profile.lastLogin}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); saveProfile(); }} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminProfile.name', 'Name')}</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  error={validationErrors.name}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminProfile.surname', 'Surname')}</label>
                <Input
                  value={editForm.surname}
                  onChange={(e) => setEditForm(prev => ({ ...prev, surname: e.target.value }))}
                  error={validationErrors.surname}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('adminProfile.email', 'Email')}</label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                error={validationErrors.email}
              />
            </div>

            <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button onClick={cancelEdit} variant="secondary">
                {t('adminProfile.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('adminProfile.saveChanges', 'Save Changes')}
              </Button>
            </div>
          </form>
        )}

        {showSuccess && (
          <div className="mt-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm">
            {t('adminProfile.profileUpdated', 'Profile updated successfully')}
          </div>
        )}
      </Card>

      {/* Notification Preferences Section */}
      <Card className="mb-5 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('adminProfile.notificationPrefs', 'Notification Preferences')}</h2>
          <Button
            onClick={() => isEditingNotifications ? cancelNotificationEdit() : startNotificationEdit()}
            variant="primary"
          >
            {isEditingNotifications ? t('adminProfile.cancel', 'Cancel') : t('adminProfile.editPrefs', 'Edit Preferences')}
          </Button>
        </div>

        {!isEditingNotifications ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.emailAlerts', 'Email Alerts')}</label>
                <p className="text-base sm:text-sm text-gray-900">
                  {notifications.emailEnabled ? t('adminProfile.enabled', 'Enabled') : t('adminProfile.disabled', 'Disabled')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.smsAlerts', 'SMS Alerts')}</label>
                <p className="text-base sm:text-sm text-gray-900">
                  {notifications.smsEnabled ? t('adminProfile.enabled', 'Enabled') : t('adminProfile.disabled', 'Disabled')}
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('adminProfile.frequency', 'Frequency')}</label>
              <p className="text-base sm:text-sm text-gray-900">{notifications.frequency}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); saveNotifications(); }} className="space-y-4">
            <div className="space-y-4">
              <div
                onClick={() => setNotificationForm(prev => ({ ...prev, emailEnabled: !prev.emailEnabled }))}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0 mr-4">
                  <div className="text-base sm:text-sm font-medium text-gray-900">{t('adminProfile.emailAlerts', 'Email Alerts')}</div>
                  <div className="text-sm sm:text-xs text-gray-500 mt-0.5">{t('adminProfile.emailAlertsDesc', 'Receive notifications via email')}</div>
                </div>
                <div className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                  notificationForm.emailEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}>
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    notificationForm.emailEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </div>

              <div
                onClick={() => setNotificationForm(prev => ({ ...prev, smsEnabled: !prev.smsEnabled }))}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0 mr-4">
                  <div className="text-base sm:text-sm font-medium text-gray-900">{t('adminProfile.smsAlerts', 'SMS Alerts')}</div>
                  <div className="text-sm sm:text-xs text-gray-500 mt-0.5">{t('adminProfile.smsAlertsDesc', 'Receive notifications via SMS')}</div>
                </div>
                <div className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                  notificationForm.smsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}>
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    notificationForm.smsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1.5">{t('adminProfile.frequency', 'Frequency')}</label>
                <select
                  value={notificationForm.frequency}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, frequency: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base sm:text-sm"
                >
                  {frequencyOptions.map(freq => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button onClick={cancelNotificationEdit} variant="secondary">
                {t('adminProfile.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('adminProfile.savePrefs', 'Save Preferences')}
              </Button>
            </div>
          </form>
        )}

        {showNotificationSuccess && (
          <div className="mt-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm">
            {t('adminProfile.notificationUpdated', 'Notification preferences updated successfully')}
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <Card>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">{t('adminProfile.accountActions', 'Account Actions')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button
            onClick={() => navigate('/admin/password-change')}
            variant="secondary"
            className="text-left justify-start"
          >
            {t('adminProfile.changePassword', 'Change Password')}
          </Button>
          <Button
            onClick={() => navigate('/admin/data-export')}
            variant="secondary"
            className="text-left justify-start"
          >
            {t('adminProfile.exportData', 'Export Data')}
          </Button>
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="secondary"
            className="text-left justify-start"
          >
            {t('adminProfile.backToDashboard', 'Back to Dashboard')}
          </Button>
          <Button
            onClick={() => navigate('/admin/delete-request')}
            variant="secondary"
            className="text-left justify-start text-red-700 hover:bg-red-50 border-red-300"
          >
            {t('adminProfile.deleteAccount', 'Delete Account')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default AdminProfilePage;
